import { Component } from '@angular/core'
import { FormBuilder, FormGroup, Validators } from '@angular/forms'
import { Store } from '@ngrx/store'
import { Subject, Subscription, catchError, map, of } from 'rxjs'
import { GlobalConstants } from 'src/app/constants/global-constants'
import {
  ActivityAPIModel, CategorySelection,
  EmployeeAPIModel, LocationAPIModel,
  TaskTypeAPIModel,
  UserAPIModel
} from 'src/app/models/api.model'
import { ActivityService } from 'src/app/services/activity/activity.service'
import { CommonService, addDaysToDate, addDaysToUIFormattedDate } from 'src/app/services/common/common.service'
import { InitializeService } from 'src/app/services/initialize/initialize.service'
import { getUserFromState } from 'src/app/store/selector/initialize.selector'
import { AppState } from 'src/app/store/state/app.state'
import {
  addDisabledFormControl,
  addFormControl,
  isFormControlAvailable,
  removeFormControl
} from 'src/app/utils/form-control.util'
import { AbstractValidator, IColumn } from '../../import/validators/abstract-validator.interface'
import {
  ACTIVITY_NAME,
  ACTIVITY_TYPE,
  CATEGORY,
  DESCRIPTION,
  DISPLAY_ID,
  DOCUMENT_NAMES,
  EMPLOYEE_CODE,
  ENTITY_ID
} from 'src/app/constants/excel.columns'

@Component({
  selector: 'app-activity',
  templateUrl: './create-activity.component.html',
  styleUrls: ['./create-activity.component.scss']
})
export class CreateActivityComponent {
  createActivityForm: FormGroup
  filesToBeUploaded: FileList

  activityTaskTypes: TaskTypeAPIModel[] = []
  activityTypes = []
  uploadedDocuments: any[]

  private readonly ACTIVITY_TYPE_CONTROL_NAME = 'activityType'
  private readonly NAME_CONTROL_NAME = 'name'
  readonly OWNER_ID_CONTROL_NAME = 'ownerId'
  readonly ASSIGNED_TO_CONTROL_NAME = 'assignedTo'
  readonly RESPONSE_DUE_DATE_TIME_CONTROL_NAME = 'responseDueDatetime'
  readonly TASK_CATEGORY_CONTROL_NAME = 'taskCategory'
  readonly TASK_SUB_CATEGORY_CONTROL_NAME = 'taskSubCategory'
  readonly LOCATION = 'location'

  selectTaskTypesSubscription$: Subscription
  user$: Subscription
  user: UserAPIModel

  readonly userType = GlobalConstants.USER_TYPE
  readonly module = GlobalConstants.MODULE.ACTIVITY_TRACKER
  selectedTaskType: TaskTypeAPIModel
  showImport = false
  importValidator: ActivityValidator
  selectedEmployeeCompanyEntityId: string = null
  locationMap: Map<string, LocationAPIModel[]>
  locationOptions: Map<string, string>

  constructor(
    private formBuilder: FormBuilder,
    private actvityService: ActivityService,
    private cs: CommonService,
    private initializeService: InitializeService,
    private appState: Store<AppState>
  ) {
    this.importValidator = new ActivityValidator(actvityService)
  }

  addAssignedToForm = addFormControl(this.ASSIGNED_TO_CONTROL_NAME, '')
  addOwnerIdToForm = addFormControl(this.OWNER_ID_CONTROL_NAME, '', [Validators.required])
  removeOwnerIdFromForm = removeFormControl(this.OWNER_ID_CONTROL_NAME)
  addResponseDueByForm = addFormControl(this.RESPONSE_DUE_DATE_TIME_CONTROL_NAME, '', [])
  addDisabledResponseDueByFrom = addDisabledFormControl(this.RESPONSE_DUE_DATE_TIME_CONTROL_NAME, '', [])
  isOwnerIdFormControlAvailable = isFormControlAvailable(this.OWNER_ID_CONTROL_NAME)

  ngOnInit() {
    this.user$ = this.appState.select(getUserFromState).subscribe((user: UserAPIModel) => {
      if (user) {
        this.user = user
        this.buildCreateActivityForm()
        this.populateOptions()
        this.addAssignedToForm(this.createActivityForm)
        if (user.userType === this.userType.EMPLOYEE_ADMIN) {
          this.addDisabledResponseDueByFrom(this.createActivityForm)
        }
      }
    })
  }

  buildCreateActivityForm() {
    this.createActivityForm = this.formBuilder.group({
      [this.NAME_CONTROL_NAME]: ['', [Validators.required]],
      [this.ACTIVITY_TYPE_CONTROL_NAME]: ['', [Validators.required]],
      description: ['', [Validators.required, Validators.minLength(5), Validators.maxLength(5000)]],
      addToCalendar: [false, [Validators.required]],
      responseDueInDays: [null, []],
      companyEntityId: ['', [Validators.required]],
      [this.TASK_CATEGORY_CONTROL_NAME]: ['', [Validators.required]],
      [this.TASK_SUB_CATEGORY_CONTROL_NAME]: ['', [Validators.required]],
      [this.RESPONSE_DUE_DATE_TIME_CONTROL_NAME]: [''],
      location: ['', [Validators.required]],
      headcount: ['', [Validators.maxLength(5), Validators.pattern(/^[0-9]*$/)]]
    })
  }

  saveAndExit() {
    if (this.createActivityForm.invalid) {
      this.createActivityForm.markAllAsTouched()
      return
    }

    const status = this.selectedTaskType?.statusList?.find(status => status?.defaultStatus)?.code

    if (!status) {
      return this.cs.showError('Status code not found')
    }

    let activity = {
      ...this.createActivityForm.getRawValue(),
      documents: this.uploadedDocuments,
      taskCode: this.selectedTaskType.code,
      status,
      name: this.selectedTaskType.label
    }

    if (this.user?.userType === this.userType.EMPLOYEE_ADMIN && this.selectedTaskType?.responseDueInDays !== -1) {
      delete activity.responseDueDatetime
      const responseDueInDays = this.selectedTaskType?.responseDueInDays
      activity = { ...activity, responseDueInDays }
    }

    if (!this.isOwnerIdFormControlAvailable(this.createActivityForm)) {
      activity = { ...activity, ownerId: this.user.id }
    }

    const files: File[] = Array.from(this.filesToBeUploaded ?? [])

    this.actvityService.dispatchSaveActivityAction(
      activity,
      files,
      this.cs.createRoutingURL([GlobalConstants.ROUTING.ACTIVITY, GlobalConstants.ROUTING.DASHBOARD])
    )
  }

  exit() {
    this.cs.route(GlobalConstants.ROUTING.ACTIVITY, GlobalConstants.ROUTING.DASHBOARD)
  }

  handleFileInput(files: FileList) {
    this.filesToBeUploaded = files
  }

  clearSelectedFiles(flag: boolean) {
    if (flag) {
      this.filesToBeUploaded = null
    }
  }

  get f() {
    return this.createActivityForm.controls
  }

  private getActivityNameAndType(categorySelection: CategorySelection) {
    const { selectedCategory, selectedSubCategory } = categorySelection

    this.selectTaskTypesSubscription$ = this.initializeService
      .getFilteredTaskTypes(GlobalConstants.MODULE.ACTIVITY_TRACKER, selectedCategory, selectedSubCategory)
      .subscribe((taskTypes: TaskTypeAPIModel[]) => {
        if (taskTypes) {
          this.activityTaskTypes = taskTypes
        }
      })
  }

  onActivityNameSelectChange(selectedValue: any) {
    this.selectedEmployeeCompanyEntityId = null
    const selectedTaskType = this.activityTaskTypes.find(taskType => taskType.code === selectedValue)
    this.selectedTaskType = selectedTaskType
    const responseDueDateTimeControl = this.createActivityForm.controls[this.RESPONSE_DUE_DATE_TIME_CONTROL_NAME]
    if (selectedTaskType.responseDueInDays !== -1) {
      responseDueDateTimeControl.setValue(selectedTaskType.responseDueInDays)
      responseDueDateTimeControl.removeValidators(Validators.required)
      responseDueDateTimeControl.updateValueAndValidity()
    } else {
      responseDueDateTimeControl.reset()
      responseDueDateTimeControl.addValidators(Validators.required)
      responseDueDateTimeControl.updateValueAndValidity()
    }

    if (this.user.userType == GlobalConstants.USER_TYPE.EMPLOYEE_ADMIN) {
      if (selectedTaskType.responseDueInDays === -1) {
        responseDueDateTimeControl.enable()
      } else {
        responseDueDateTimeControl.disable()
      }
    }

    if (selectedTaskType.offlineTask && selectedTaskType.onlineTask) {
      this.setActivityTypesAndEnableControl([GlobalConstants.TASK_TYPE.ONLINE, GlobalConstants.TASK_TYPE.OFFLINE])
    } else if (selectedTaskType.onlineTask) {
      this.setActivityTypesAndDisableControl(GlobalConstants.TASK_TYPE.ONLINE)
    } else if (selectedTaskType.offlineTask) {
      this.setActivityTypesAndDisableControl(GlobalConstants.TASK_TYPE.OFFLINE)
    }

    if (selectedTaskType.employeeLinkedTask) {
      this.addOwnerIdToForm(this.createActivityForm)
    } else {
      this.removeOwnerIdFromForm(this.createActivityForm)
    }

    const responseDueInDays = selectedTaskType?.responseDueInDays

    if (responseDueInDays != null && this.cs.isNumber(responseDueInDays) && responseDueInDays !== -1) {
      const date =
        this.user.userType == GlobalConstants.USER_TYPE.EMPLOYEE_ADMIN
          ? addDaysToUIFormattedDate(Number(responseDueInDays))
          : addDaysToDate(Number(responseDueInDays))
      this.createActivityForm.patchValue({ [this.RESPONSE_DUE_DATE_TIME_CONTROL_NAME]: date })
    }
  }

  private setActivityTypesAndEnableControl(types: string[]) {
    this.activityTypes = types
    this.enableActivityTypeControl()
  }

  private setActivityTypesAndDisableControl(type: string) {
    this.activityTypes = [type]
    this.disableActivityTypeControl(type)
  }

  private enableActivityTypeControl() {
    this.createActivityForm.controls[this.ACTIVITY_TYPE_CONTROL_NAME].enable()
  }

  private disableActivityTypeControl(activityType: string) {
    this.createActivityForm.controls[this.ACTIVITY_TYPE_CONTROL_NAME].setValue(activityType)
    this.createActivityForm.controls[this.ACTIVITY_TYPE_CONTROL_NAME].disable()
  }

  onCategorySelected(selectedCategorySelection: CategorySelection) {
    const { selectedCategory, selectedSubCategory } = selectedCategorySelection
    this.disableActivityTypeControl('')
    this.createActivityForm.controls[this.RESPONSE_DUE_DATE_TIME_CONTROL_NAME].reset()
    this.createActivityForm.controls[this.NAME_CONTROL_NAME].enable()

    this.createActivityForm.get(this.NAME_CONTROL_NAME).markAsUntouched()
    this.createActivityForm.get(this.ACTIVITY_TYPE_CONTROL_NAME).markAsUntouched()

    this.createActivityForm.get(this.NAME_CONTROL_NAME).setValue('')
    this.createActivityForm.get(this.ACTIVITY_TYPE_CONTROL_NAME).setValue('')

    this.activityTaskTypes = []
    this.activityTypes = []
    this.createActivityForm.get(this.TASK_CATEGORY_CONTROL_NAME).setValue(selectedCategory)
    this.createActivityForm.get(this.TASK_SUB_CATEGORY_CONTROL_NAME).setValue(selectedSubCategory)
    this.getActivityNameAndType(selectedCategorySelection)
  }

  handleAssignToSelectedValue(assignedTo: string) {
    this.createActivityForm.patchValue({ assignedTo })
  }

  handleEmployeeSelectedValue(ownerId: string) {
    this.createActivityForm.patchValue({ ownerId })
  }

  handleCompanySelectedValue(companyEntityId: string) {
    this.createActivityForm.patchValue({ companyEntityId })
    this.loadLocationsForCompany(companyEntityId)
  }

  handleEmployeeSelected(employee: EmployeeAPIModel) {
    this.selectedEmployeeCompanyEntityId = employee?.companyEntityId
  }

  populateOptions() {
    this.initializeService.getRelatedLocationMap().subscribe({
      next: locationMap => {
        if (locationMap?.size > 0) {
          this.locationMap = locationMap
        }
      }
    })
  }

  loadLocationsForCompany(companyEntityId: string) {
    if (companyEntityId && this.locationMap?.size > 0) {
      const locationAPIModelArr: LocationAPIModel[] = this.locationMap.get(companyEntityId)
      if (locationAPIModelArr) {
        const locationMap = new Map<string, string>()
        locationAPIModelArr?.forEach(locationAPIModel => {
          locationMap.set(locationAPIModel.code, locationAPIModel.code)
        })
        this.locationOptions = locationMap
      }
    }
    const locationControl = this.createActivityForm.get(this.LOCATION)
    locationControl?.setValue('')
    locationControl?.updateValueAndValidity()
  }

  ngOnDestroy() {
    this.selectTaskTypesSubscription$?.unsubscribe()
    this.user$?.unsubscribe()
  }
}

export class ActivityValidator extends AbstractValidator<ActivityAPIModel> {
  public override allRows: Subject<ActivityAPIModel[]> = new Subject<ActivityAPIModel[]>()
  public override title = 'Activity'
  public override templateName: string = 'Activity_Template.xlsx'
  public override templateURL: string = ''
  public override saveResponse: Subject<any> = new Subject<any>()
  public override _header: Map<string, IColumn> = new Map([
    ['Entity ID', ENTITY_ID],
    ['Display ID', DISPLAY_ID],
    ['Category', CATEGORY],
    ['Activity Name', ACTIVITY_NAME],
    ['Activity Type', ACTIVITY_TYPE],
    ['Employee Code', EMPLOYEE_CODE],
    ['Description', DESCRIPTION],
    ['Document Names', DOCUMENT_NAMES]
  ])

  public override document: { isRequired: boolean; showUpload: boolean; file$: Subject<File> } = {
    isRequired: false,
    showUpload: true,
    file$: new Subject<File>()
  }

  public override startImporting$: Subject<boolean> = new Subject<boolean>()
  public override apiResponse$: Subject<any> = new Subject<boolean>()
  private rowsToSave = []
  private documentToSave = null
  constructor(private activityService: ActivityService, u: ActivityAPIModel = null) {
    super(u)

    this.allRows.subscribe(rowsToSave => {
      this.rowsToSave = rowsToSave
    })

    this.document.file$.subscribe((file: File) => {
      this.documentToSave = file
    })

    this.subscribeImportStart()
  }

  private subscribeImportStart() {
    this.startImporting$.subscribe(doStartImporting => {
      if (doStartImporting) {
        const activities = this.createActivitiesList()
        this.activityService
          .invokeSaveBulkActivityAPI(activities, this.documentToSave)
          .pipe(
            map((response: any) => {
              this.apiResponse$.next({ status: 1, response: { ...response, fileName: 'activity.xlsx' } })
            }),
            catchError((error: any) => {
              this.apiResponse$.next({ status: 0, error })
              return of(null)
            })
          )
          .subscribe()
      }
    })
  }

  private createActivitiesList(): ActivityAPIModel[] {
    const activities = []
    for (const row of this.rowsToSave) {
      const activity = {}
      for (const key of Object.keys(row)) {
        if (key) {
          if (key === DOCUMENT_NAMES.key && row[key]) {
            const documents = []
            if (row[key].includes(',')) {
              const documentSplits = row[key].split(',')
              for (const documentSplit of documentSplits) {
                documents.push(documentSplit.trim())
              }
            } else {
              documents.push(row[key].trim())
            }
            activity[key] = documents
            continue
          }
          if (!row[key] && key === DOCUMENT_NAMES.key) {
            activity[key] = []
          } else {
            activity[key] = row[key]
          }
        }
      }
      activities.push(activity)
    }
    return activities
  }
}
